import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { sortBy } from 'lodash';
import { ChannelService } from 'src/app/core/service/channel.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Guid } from 'guid-typescript';
import { CaseUtility } from 'src/app/case/case.utility';
@Component({
  selector: 'app-formly-designer-channel-edit',
  templateUrl: './formly-designer-channel-edit.component.html',
  styleUrls: ['./formly-designer-channel-edit.component.scss']
})
export class FormlyDesignerChannelEditComponent implements OnInit
{
  @Input() public fields: FormlyFieldConfig[];
  @Input() public isDeleted = false;

  @Output() public formChanged: EventEmitter<any> = new EventEmitter();

  public editForm: any;
  public channels: UntypedFormArray;
  public allChannels = [];
  public groupedChannels: GroupResult[] = [];
  public loaded = false;

  /**
   * Get the list of priority options for Cases.
   */
  public priorities = [];

  constructor(
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private caseUtilityService: CaseUtility,
    private channelService: ChannelService) { }

  @Input() public set model(serviceTypes: any)
  {
    this.initModel(serviceTypes);
  }

  public ngOnInit(): void
  {
    this.loaded = false;
    this.priorities = this.caseUtilityService.distinctPriorities;
  }

  /**
   * Check if item from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.status !== 'Online';
  }

  /**
   * Add a service type entry
   */
  public addChannel(id: number = null, version = 0, channel: any = null, priority: any = null,
    query: any = null, showRules = true, importError = false): void
  {
    this.editForm.controls.channels.push(this.initChannel(id, version, channel, priority, query, showRules, importError));
    if (id === null)
    {
      this.editForm.markAsDirty();
    }
  }

  /**
   * Remove a service type entry
   *
   * @param i Index
   */
  public removeChannel(i: number): void
  {
    const control = this.editForm.controls.channels;
    control.removeAt(i);
    this.editForm.markAsDirty();
  }

  public showRulesChanged(e: any): void
  {
    this.editForm.controls.channels.controls[e.index].controls.showRules.value = e.show;
    // If the rule is opened, we assumed a state changes.
    if (!this.isDeleted)
    {
      this.editForm.markAsDirty();
      this.formChanged.emit(this.editForm);
    }
  }

  /**
   * Used for the trackBy to prevent rendering the DOM and losing field focus.
   *
   * @param index Row index.
   * @param item Row item.
   */
  public channelsTrackBy(index: number, item: any): string
  {
    return item.value.guid;
  }

  /**
   * Disable controls when Assessement is deleted
   */
  private disableControls(): void
  {
    if (this.isDeleted)
    {
      this.editForm.controls.channels.controls.forEach(data =>
      {
        data.controls.channel.disable();
        data.controls.priority.disable();
      });
    }
  }

  /**
   * Model initilization
   *
   * @param channels Assessment channels edit object.
   */
  private initModel(channels: any): void
  {
    if (channels === undefined || this.loaded)
    {
      return;
    }

    // Build form
    this.editForm = this.fb.group({
      channels: this.fb.array([])
    });

    this.channelService.getCacheMinimalChannels('?nonPrivateOnly=true').subscribe({
      next: (channelData) =>
      {
        this.allChannels = channelData;
        this.allChannels.forEach(d =>
        {
          d.programName = d.program.name;
        });

        // Set the form
        if (channels && channels.length > 0)
        {
          channels.forEach(data =>
          {
            this.addChannel(data.id, data.version, data.channel, data.priority, data.query, false, data.importError);

            // Check if Channel is still online, if not we need to fetch this program separatly.
            const channelId = data.channel ? data.channel.id : null;
            if (channelId !== null)
            {
              const channelFound = this.allChannels.filter(x => x.id === channelId);
              if (channelFound.length === 0)
              {
                this.channelService.getMinimalChannelById(channelId).subscribe({
                  next: (lookupChannel) =>
                  {
                    if (lookupChannel.length === 1)
                    {
                      lookupChannel.forEach(d =>
                      {
                        d.programName = d.program.name;
                      });

                      // Add Channel to allChannels array to show but will not be able to select (will be disabled)
                      this.allChannels.push(lookupChannel[0]);
                      this.setGroupedChannels();
                    }
                    else
                    {
                      // Skip the load since we can't retrieve the channelId.
                      this.messageHandlerService.displayWarning('CHANNEL-NOT-FOUND');
                    }
                  }
                });
              }
            }
          });
        }

        this.editForm.valueChanges.subscribe({
          next: () =>
          {
            if (!this.isDeleted)
            {
              this.formChanged.emit(this.editForm);
            }
          }
        });

        // Must apply a delay here because isDeleted is not set yet when setting the model.
        setTimeout(() =>
        {
          this.disableControls();
          this.setGroupedChannels();
          this.loaded = true;
        });
      },
      error: errorResponse =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels = groupBy(this.allChannels, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }

  /**
   * Create a form group.
   */
  private initChannel(id: number, version: number, channel: any, priority: any, query: any, showRules: boolean,
    importError: boolean): UntypedFormGroup
  {
    if (query === null)
    {
      query = {
        condition: 'and',
        rules: []
      };
    }
    return this.fb.group({
      guid: Guid.create().toString(),
      id: [id],
      version: [version],
      channel: [channel, Validators.required],
      priority: [priority, Validators.required],
      query: [query, Validators.required],
      showRules: [showRules],
      importError: [importError]
    });
  }
}
